import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ConstructionWaitlistController } from '../hooks/useConstructionWaitlist';

interface ConstructionWaitlistBubbleProps {
  controller: ConstructionWaitlistController;
}

const BubbleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  width: 100%;
`;

const Card = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #ffffff, #f8fafc);
  border-radius: 18px 18px 18px 0;
  padding: 1.15rem 1.25rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.18);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  text-transform: lowercase;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: #475569;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 8px;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: #111827;
    background: rgba(148, 163, 184, 0.15);
  }
`;

const PromptPreview = styled.div`
  background: rgba(226, 232, 240, 0.6);
  color: #334155;
  border-radius: 12px;
  padding: 0.6rem 0.8rem;
  font-size: 0.85rem;
  line-height: 1.4;
  text-transform: none;

  strong {
    text-transform: lowercase;
    font-weight: 600;
    color: #1e293b;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.78rem;
  text-transform: lowercase;
  color: #6b7280;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${props => props.$hasError ? '#f87171' : 'rgba(148, 163, 184, 0.45)'};
  font-size: 0.9rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: lowercase;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #ef4444;
  text-transform: lowercase;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.65rem 1.1rem;
  border-radius: 999px;
  border: ${props => props.$variant === 'primary' ? 'none' : '1px solid rgba(148, 163, 184, 0.6)'};
  background: ${props => props.$variant === 'primary' ? 'linear-gradient(135deg, #111827, #1f2937)' : '#ffffff'};
  color: ${props => props.$variant === 'primary' ? '#ffffff' : '#1f2937'};
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: lowercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  }
`;

const SuccessMessage = styled.div`
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(96, 165, 250, 0.35);
  color: #1e3a8a;
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const OptionalToggle = styled.button`
  align-self: flex-start;
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: #ffffff;
  color: #1f2937;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: lowercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(148, 163, 184, 0.12);
    border-color: rgba(148, 163, 184, 0.6);
  }
`;

const OptionalGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const ConstructionWaitlistBubble: React.FC<ConstructionWaitlistBubbleProps> = ({ controller }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const [showExtras, setShowExtras] = useState(false);
  const [addingAnother, setAddingAnother] = useState(false);

  useEffect(() => {
    if (controller.visible) {
      window.setTimeout(() => {
        try {
          emailInputRef.current?.focus();
        } catch {}
      }, 120);
      window.setTimeout(() => {
        try {
          wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {}
      }, 160);
    }
  }, [controller.visible]);

  useEffect(() => {
    if (controller.success) {
      setAddingAnother(false);
    }
  }, [controller.success]);

  if (!controller.visible) return null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await controller.submit();
  };

  const startAnother = () => {
    controller.beginAnotherEntry();
    setAddingAnother(true);
    setShowExtras(false);
  };

  return (
    <BubbleWrapper ref={wrapperRef}>
      <Card>
        <Header>
          <HeaderText>
            <Title>prest0 update</Title>
            <Subtitle>{controller.responseText}</Subtitle>
          </HeaderText>
        </Header>

        {/* prompt preview intentionally hidden as requested */}

        {controller.hasPersistedSuccess && (
          <SuccessMessage>
            you're on the early access wait list. we'll review your submission and reach out as new testing spots open ahead of the may 2026 launch. you're seeing this in stealth—please keep it confidential.
          </SuccessMessage>
        )}

        {(addingAnother || !controller.success) && (
          <Form onSubmit={handleSubmit}>
            <Field>
              <Label htmlFor="waitlist-email">email address</Label>
              <Input
                id="waitlist-email"
                ref={emailInputRef}
                type="email"
                placeholder="name@example.com"
                value={controller.email}
                onChange={(event) => controller.setEmail(event.target.value)}
                disabled={controller.submitting}
                $hasError={Boolean(controller.error)}
              />
              <Hint>email is required so we can notify you when spots open.</Hint>
              {controller.error && <ErrorText role="alert">{controller.error}</ErrorText>}
            </Field>
            <OptionalToggle
              type="button"
              onClick={() => setShowExtras(prev => !prev)}
              aria-expanded={showExtras}
              aria-controls="waitlist-optional-fields"
              disabled={controller.submitting}
            >
              {showExtras ? 'hide optional details' : 'optional details'}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={showExtras ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
              </svg>
            </OptionalToggle>
            {showExtras && (
              <OptionalGroup id="waitlist-optional-fields">
                <Field>
                  <Label htmlFor="waitlist-first-name">first name (optional)</Label>
                  <Input
                    id="waitlist-first-name"
                    type="text"
                    placeholder="first name"
                    value={controller.firstName}
                    onChange={(event) => controller.setFirstName(event.target.value)}
                    disabled={controller.submitting}
                  />
                </Field>
                <Field>
                  <Label htmlFor="waitlist-linkedin">linkedin profile (optional)</Label>
                  <Input
                    id="waitlist-linkedin"
                    type="text"
                    inputMode="url"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="linkedin.com/in/your-profile"
                    value={controller.linkedin}
                    onChange={(event) => controller.setLinkedin(event.target.value)}
                    disabled={controller.submitting}
                  />
                </Field>
                <Field>
                  <Label htmlFor="waitlist-x">x / twitter (optional)</Label>
                  <Input
                    id="waitlist-x"
                    type="text"
                    placeholder="https://x.com/username or @username"
                    value={controller.xHandle}
                    onChange={(event) => controller.setXHandle(event.target.value)}
                    disabled={controller.submitting}
                  />
                </Field>
              </OptionalGroup>
            )}
            <Actions>
              <Button type="submit" $variant="primary" disabled={controller.submitting}>
                {controller.submitting ? 'joining…' : 'join wait list'}
              </Button>
            </Actions>
          </Form>
        )}

        {controller.hasPersistedSuccess && (
          <Actions style={{ justifyContent: 'flex-start' }}>
            <Button type="button" $variant="secondary" onClick={startAnother} disabled={controller.submitting}>
              add another contact
            </Button>
          </Actions>
        )}

        {controller.reminderDue && controller.hasPersistedSuccess && (
          <SuccessMessage>
            it's been a day since you joined—add another email or contact so we can prioritize early access. tap "add another contact" to submit a fresh entry.
          </SuccessMessage>
        )}
      </Card>
    </BubbleWrapper>
  );
};

export default ConstructionWaitlistBubble;

